import type { ObjectId, WithId } from 'mongodb';

import type { Applicant } from '../AccountTypes';
import type { Meta } from '../Entities';
import type { IsoDateString, IsoDateTimeString } from '../Helpers';
import type { ProgramType } from '../ProgramTypes';
import type { QuoteDocument } from '../Quotes';

import type { ProjectClawbackRuleNames } from './Clawbacks';
import { type PayoutEvent, PayoutEventType } from './Events';
import type { PayeeDocument, PayeeType } from './Payees';

export interface BatchReview {
  status: BatchReviewStatus.approved | BatchReviewStatus.rejected | BatchReviewStatus.unapproved;
  user: { id: string; name: string };
  date: Date;
  changedFrom: any;
  changedTo: any;
}

export enum PaymentTransactionProvider {
  bofa = 'bofa',
}

export enum bofaTransactionStatus {
  processingByBank = 'Processing By Bank',
  rejected = 'Rejected',
  settlementComplete = 'Settlement Complete',
  approved = 'Approved',
  cancelled = 'Cancelled',
  receivedByBank = 'Received By Bank',
}

export enum BatchReviewStatus {
  approved = 'approved',
  unapproved = 'unapproved',
  rejected = 'rejected',
}

export enum BatchType {
  vendor = 'vendor',
  organization = 'organization',
  holdback = 'holdback',
}

type CreateAdhocBatchBase = {
  batchType: BatchType;
  batchDate: IsoDateString;
};

/**
 * Adhoc batch creation is mutually exclusive: either manual (an explicit set of
 * transactionIds) or auto (everything unbatched up to a cutoff). The discriminated
 * union forces callers to send exactly one mode, never both and never neither.
 */
export type CreateAdhocBatchPayload =
  | (CreateAdhocBatchBase & { transactionIds: string[]; cutoffDateTimeIso?: never }) // manual
  | (CreateAdhocBatchBase & { cutoffDateTimeIso: IsoDateTimeString; transactionIds?: never }); // auto

export const BatchTypeToPayoutEventTypeMap: Record<BatchType, Array<PayoutEventType>> = {
  [BatchType.vendor]: [PayoutEventType.materials],
  [BatchType.organization]: [
    PayoutEventType.materials,
    PayoutEventType.milestone,
    PayoutEventType.deduction,
    PayoutEventType.credit,
  ],
  [BatchType.holdback]: [PayoutEventType.holdback],
};

export interface EventSummary {
  amount: number;
  numberOfTransactions: number;
}

export interface BatchSummarySnapshot {
  amount: number;
  numberOfTransactions: number;
  [key: string]: number | EventSummary;
}

export interface BatchSummaryDocument {
  id?: string;
  payees: BatchGroupCandidate[];
  batchIds: ObjectId[];
  batchType: BatchType;
  batchDate: IsoDateString;
  cutoffDate: Date;
  summary: BatchSummarySnapshot;
  meta?: Meta;
}

export interface BatchHistoryEntry {
  date: Date;
  user: { id: string; name: string };
  summary: string;
}

export interface BatchDocument {
  id?: string;
  payeeId: ObjectId;
  payeeExternalId: string;
  payeeType: PayeeType;
  batchType: BatchType;
  bankTransaction?: {
    provider: PaymentTransactionProvider;
    id: string;
    status: bofaTransactionStatus;
    statusHistory: Array<{ id: string; status: bofaTransactionStatus; statusDate: Date }>;
  };
  review?: BatchReview;
  reviewHistory?: Array<BatchReview>;
  batchDate: IsoDateString;
  cutoffDate?: Date;
  isAdhoc?: boolean;
  history?: BatchHistoryEntry[];
  meta?: Meta;
}

export interface BatchQueryParams {
  batchIds?: Array<string | ObjectId>;
  batchReviewStatus?: BatchReviewStatus;
  startCutoffDate?: IsoDateString;
  endCutoffDate?: IsoDateString;
  payeeExternalId?: string | Record<string, any>;
  payeeId?: string | ObjectId;
  payeeType?: PayeeType | Record<string, any>;
  batchType?: BatchType;
  programType?: ProgramType;
  batchDate?: IsoDateString;
  projectOwnerExternalIds?: string[];
  format?: 'csv' | 'json';
}

export interface BatchGroupInfo {
  batchDate: IsoDateString;
  payeeType: PayeeType;
  batchType: BatchType;
  batchIds: ObjectId[];
  batchReviewStatus: BatchReviewStatus;
  payeeCount: number;
  transactionCount: number;
  transactionSum: number;
  cutoffDateTime: Date;
}

export interface BatchGroupCandidate {
  _id: ObjectId; // payeeId
  payee: WithId<PayeeDocument>;
  count: number;
  total: number;
  payeeType: PayeeType;
  transactions: Array<{
    transactionId: ObjectId;
    payoutEvent: PayoutEvent;
    amount: number;
    clawbackRuleName?: ProjectClawbackRuleNames;
  }>;
}

export interface BatchExportInfo {
  finCoAccountId: string;
  transactionId: string;
  transactionLevel: string;
  generalLedgerCategory: string;
  transactionDate: string;
  payoutDate: string;
  amountToPay: string;
  paymentPlanEventPercentage: string;
  paymentPlanName: string;
  payee: string;
  licensedOrganizationName: string;
  organizationAlias: string;
  salesRepName: string;
  primaryApplicant: Applicant;
  quoteDetails: QuoteDocument;
  kwhRate: string;
  cedInvoiceAmountSum: string;
  accountCreatedAtDate: string;
  contractSignedAt: string;
  inverterManufacturer: string;
  utilityName: string;
  hasBattery: string;
  directPayProjectIdentifier: string;
  noticeToProceedApprovedAtDate: string;
  ntpPlusSubmittedAtDate: string;
  installApprovedAtDate: string;
  installSubmittedAtDate: string;
  systemActivationApprovedAtDate: string;
  organizationPlanId?: string;
  approvedDate: string;
  payoutEvent: string;
  description: string;
}

export type PreviewAdhocBatchParams =
  | { batchType: BatchType; batchDate: IsoDateString; cutoffDateTimeIso: string }
  | { transactionIds: string[]; targetBatchDate: IsoDateString };

export interface AdhocBatchPreviewPayee {
  payeeId: string;
  payeeExternalId: string;
  batchType: BatchType;
  transactionCount: number;
  newAmount: number;
  existingAmount: number;
  combinedTotal: number;
}

export interface AdhocBatchPreviewResult {
  payees: AdhocBatchPreviewPayee[];
  summary: {
    payeeCount: number;
    transactionCount: number;
    totalAmount: number;
  };
  hasNegativePayeeBatch: boolean;
}
